import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Inbox, List, ShoppingBag, Bell, Heart, Sparkles, Users, BarChart3, Wrench, ArrowRightLeft, ScanLine, Mic, FileText, Network, Tag, Gavel, Repeat, Truck, Command, Compass, ClipboardCheck, Camera, Zap, Receipt, Package, Building2, ShieldAlert, ShieldCheck, Handshake, Star, Coins, Rocket } from "lucide-react";
import api from "../lib/api";

/**
 * Feb-2026 — Globale Command-Palette (Cmd+K / Ctrl+K / „/").
 *
 * Vereint:
 *  • Schnell-Aktionen (Inserat anlegen, Inbox öffnen, …)
 *  • Live-Suche über eigene Inserate
 *  • Direktsprung zu Dashboard-Tabs
 *
 * Reduziert 5-Klick-Workflows auf 1 Eingabe.
 */
const ACTIONS = [
    // — Wichtigste Aktionen (immer ganz oben)
    { id: "go-inbox", icon: Inbox, label: "Smart-Inbox", hint: "Was zu tun ist", url: "/dashboard?tab=inbox", roles: ["b2c", "b2b", "vermittler"], rank: 100 },
    { id: "create-listing", icon: Plus, label: "Fahrzeug verkaufen", hint: "Neues Fahrzeug einstellen", url: "/create-listing", roles: ["b2b", "b2c"], rank: 99 },
    { id: "zb1-scan", icon: ScanLine, label: "ZB1 / COC scannen", hint: "KI füllt 28 Felder automatisch", url: "/create-listing?action=zb1-scan", roles: ["b2b", "b2c"], rank: 98 },
    { id: "voice-listing", icon: Mic, label: "Voice-to-Listing", hint: "Inserat per Sprache diktieren", url: "/create-listing?action=voice", roles: ["b2b", "b2c"], rank: 97 },
    { id: "dealer-workshop-quick", icon: Zap, label: "Schnell-Erfassung (Werkstatt)", hint: "Auftrag in Sekunden an der Theke anlegen", url: "/dealer/workshop-orders?action=quick-capture", roles: ["b2b", "zweiradverkaeufer"], rank: 96 },
    { id: "vehicle-lookup", icon: Search, label: "Fahrzeug finden (Kennzeichen / FIN)", hint: "Scannen oder tippen → vorhandenes Fahrzeug zum Auftrag", url: "__action:vehicle-lookup", roles: ["b2b"], rank: 95 },

    // — Inserate / Marktplatz
    { id: "go-listings", icon: List, label: "Meine Inserate", hint: "Inventory-Cockpit & Liste", url: "/dashboard?tab=listings", roles: ["b2c", "b2b"] },
    { id: "go-search", icon: Search, label: "Fahrzeuge suchen", hint: "Marktplatz mit KI-Smart-Search", url: "/search", roles: ["b2c", "b2b", "vermittler"] },
    { id: "go-favorites", icon: Heart, label: "Favoriten", hint: "Gemerkte Fahrzeuge", url: "/dashboard?tab=favorites", roles: ["b2c", "b2b"] },
    { id: "go-saved-searches", icon: Bell, label: "Gespeicherte Suchen", hint: "Preis-Wecker / Alerts", url: "/dashboard?tab=saved-searches", roles: ["b2c", "b2b", "vermittler"] },
    { id: "go-compare", icon: ArrowRightLeft, label: "Fahrzeugvergleich", hint: "Bis zu 3 Fahrzeuge gegenüberstellen", url: "/compare", roles: ["b2c", "b2b", "vermittler"] },

    // — Handel
    { id: "go-offers", icon: ShoppingBag, label: "Preisvorschläge", hint: "Offene Verhandlungen", url: "/dashboard?tab=offers", roles: ["b2c", "b2b"] },
    { id: "go-reservations", icon: Tag, label: "Verkäufe / Reservierungen", hint: "Aktive Geschäfte", url: "/dashboard?tab=reservations", roles: ["b2c", "b2b"] },
    { id: "go-seller-reservations", icon: Tag, label: "Eingehende Reservierungen", hint: "Käuferanfragen bestätigen", url: "/dashboard?tab=seller-reservations", roles: ["b2b"] },
    { id: "go-tradeins", icon: Repeat, label: "Inzahlungnahme / Tausch", hint: "Tauschangebote", url: "/dashboard?tab=tradeins", roles: ["b2c", "b2b"] },
    { id: "go-buy-requests", icon: ShoppingBag, label: "Ankäufe (Privat)", hint: "B2C-Ankaufsanfragen", url: "/dashboard?tab=buy-requests", roles: ["b2b"] },
    { id: "go-transports", icon: Truck, label: "Offene Transporte", hint: "Belegcheck inkl.", url: "/dashboard?tab=open-transports", roles: ["b2b"] },

    // — Auktionen / KI
    { id: "go-auctions", icon: Gavel, label: "Aktive Auktionen", hint: "Top-Gebote sichern", url: "/auctions", roles: ["b2c", "b2b"] },
    { id: "go-ai", icon: Sparkles, label: "KI-Features", hint: "ZB1, Voice, Smart-Search, …", url: "/ki", roles: ["b2c", "b2b", "vermittler"] },
    { id: "go-workshops", icon: Wrench, label: "Werkstatt buchen", hint: "Service-Termin reservieren", url: "/werkstaetten", roles: ["b2c", "b2b"] },

    // — Rollen-spezifisch
    { id: "go-vermittler", icon: Network, label: "Vermittler & Verkäufer-Dashboard", hint: "Punkte, Empfehlungen, Smily", url: "/vermittler", roles: ["vermittler"], rank: 90 },
    { id: "go-admin", icon: BarChart3, label: "Admin-Dashboard", hint: "Plattform-Übersicht & Moderation", url: "/admin", roles: ["admin"], rank: 95 },

    // — Händler-ERP (B2B, Jun-2026): tiefe Werkstatt-/WMS-/Rechnungs-/Dispo-Aktionen
    { id: "dealer-disposition", icon: Truck, label: "Disposition", hint: "Bestand, Standtage, Verkaufsabwicklung", url: "/dealer/disposition", roles: ["b2b"] },
    { id: "dealer-inventory", icon: ClipboardCheck, label: "Fahrzeuginventur", hint: "FIN/QR-Zählung", url: "/dealer/fahrzeuginventur", roles: ["b2b"] },
    { id: "dealer-inventory-uncounted", icon: ClipboardCheck, label: "Nie gezählte Fahrzeuge", hint: "Offene Inventur", url: "/dealer/fahrzeuginventur?filter=uncounted", roles: ["b2b"] },
    { id: "dealer-workshop-orders", icon: Wrench, label: "Werkstatt-Aufträge", hint: "Aufträge-Übersicht", url: "/dealer/workshop-orders", roles: ["b2b", "zweiradverkaeufer"] },
    { id: "dealer-workshop-new", icon: Plus, label: "Neuer Werkstatt-Auftrag", hint: "Auftrag anlegen", url: "/dealer/workshop-orders?action=new", roles: ["b2b", "zweiradverkaeufer"] },
    { id: "dealer-workshop-zb1", icon: Camera, label: "Fahrzeug scannen (ZB1)", hint: "Fahrzeugschein per Kamera scannen", url: "/dealer/workshop-orders?action=zb1-scan", roles: ["b2b", "zweiradverkaeufer"], rank: 94 },
    { id: "dealer-workshop-calendar", icon: Wrench, label: "Werkstatt-Kalender", hint: "Termine & Ressourcen", url: "/dealer/workshop-calendar", roles: ["b2b", "zweiradverkaeufer"] },
    { id: "dealer-invoices", icon: FileText, label: "Rechnungen", hint: "Rechnungs-Übersicht", url: "/dealer/invoices", roles: ["b2b"] },
    { id: "dealer-new-invoice", icon: FileText, label: "Neue Rechnung", hint: "Faktura anlegen", url: "/dealer/invoices?action=new-invoice", roles: ["b2b"] },
    { id: "dealer-direct-sale", icon: Receipt, label: "Direktverkauf", hint: "Barverkauf / Kasse", url: "/dealer/invoices?action=direct-sale", roles: ["b2b"] },
    { id: "dealer-datev", icon: FileText, label: "DATEV-Export", hint: "Buchhaltung / Steuerberater", url: "/dealer/invoices?action=datev", roles: ["b2b"] },
    { id: "dealer-wms", icon: Package, label: "Warenwirtschaft", hint: "Lager & Artikel", url: "/dealer/wms", roles: ["b2b"] },
    { id: "dealer-new-article", icon: Plus, label: "Neuer Artikel", hint: "Lagerartikel anlegen", url: "/dealer/wms?action=new-article", roles: ["b2b"] },
    { id: "dealer-scan-barcode", icon: ScanLine, label: "Barcode scannen", hint: "Wareneingang / EAN", url: "/dealer/wms?action=scan-barcode", roles: ["b2b"] },
    { id: "dealer-scan-delivery", icon: FileText, label: "Lieferschein scannen", hint: "Wareneingang per KI", url: "/dealer/wms/scan-delivery", roles: ["b2b"] },
    { id: "dealer-purchase-orders", icon: Truck, label: "Bestellungen", hint: "Lieferanten / Einkauf", url: "/dealer/purchase-orders", roles: ["b2b"] },
    { id: "dealer-intranet", icon: Building2, label: "Intranet", hint: "Interne Infos & Downloads", url: "/intranet", roles: ["b2b"] },

    // — Vermittler-spezifisch (Jun-2026)
    { id: "vermittler-commissions", icon: Coins, label: "Meine Provisionen", hint: "Verdienst & Abrechnung", url: "/vermittler/commissions", roles: ["vermittler"] },

    // — Admin-Verwaltung (Jun-2026)
    { id: "admin-dealers", icon: ShieldAlert, label: "Händler-Verwaltung", hint: "Freigabe & Audit", url: "/admin/dealers", roles: ["admin"] },
    { id: "admin-vermittlers", icon: ShieldAlert, label: "Vermittler-Verwaltung", hint: "Tippgeber", url: "/admin/vermittlers", roles: ["admin"] },
    { id: "admin-referrals", icon: Handshake, label: "Empfehlungs-Leads", hint: "Referral-Tracking", url: "/admin/vermittler-referrals", roles: ["admin"] },
    { id: "admin-service-providers", icon: Handshake, label: "Dienstleister", hint: "Verzeichnis & Freigabe", url: "/admin/service-providers", roles: ["admin"] },
    { id: "admin-tickets", icon: Inbox, label: "Support-Tickets", hint: "Händler-Anfragen", url: "/admin/tickets", roles: ["admin"] },
    { id: "admin-compliance", icon: ShieldCheck, label: "Compliance", hint: "Spam, Audit, Recht", url: "/admin/compliance", roles: ["admin"] },
    { id: "admin-reviews", icon: Star, label: "Bewertungs-Moderation", hint: "Sterne & Feedback", url: "/admin/reviews", roles: ["admin"] },
    { id: "admin-points", icon: Coins, label: "€Punkte-Verwaltung", hint: "Punkte vergeben", url: "/admin/points", roles: ["admin"] },
    { id: "admin-wallet", icon: Coins, label: "Wallet-Verwaltung", hint: "Guthaben & Auszahlungen", url: "/admin/wallet", roles: ["admin"] },
    { id: "admin-pawn", icon: Coins, label: "Pfand & Refinanzierung", hint: "Pfand / Bank", url: "/admin/pawn", roles: ["admin"] },
    { id: "admin-tariffs", icon: FileText, label: "Tarife", hint: "Abo-Preise (Stripe)", url: "/admin/tariffs", roles: ["admin"] },
    { id: "admin-datev", icon: ShieldCheck, label: "DATEV-Auto-Versand", hint: "Cron & Status", url: "/admin/datev/auto-send", roles: ["admin"] },
    { id: "admin-sms", icon: Bell, label: "SMS-Verlauf", hint: "Twilio-Historie", url: "/admin/sms/history", roles: ["admin"] },
    { id: "admin-portal-consent", icon: Bell, label: "Portal-Zustimmungen", hint: "AGB & Datenschutz Log", url: "/admin/portal-consent", roles: ["admin"] },
    { id: "admin-cron", icon: ShieldCheck, label: "Cron-Status", hint: "Hintergrund-Jobs", url: "/admin/cron-status", roles: ["admin"] },
    { id: "admin-health", icon: ShieldCheck, label: "Health-Monitor", hint: "twowheels.info Produktions-Checks", url: "/admin/health-monitor", roles: ["admin"] },
    { id: "admin-vehicle-dupes", icon: ShieldCheck, label: "Fahrzeug-Doubletten", hint: "Doppelte Kundenfahrzeuge prüfen & bereinigen", url: "/admin/vehicle-duplicates", roles: ["admin"] },
    { id: "admin-customer-dupes", icon: ShieldCheck, label: "Kunden-Doubletten", hint: "Doppelte Kundendatensätze zusammenführen", url: "/admin/customer-duplicates", roles: ["admin"] },
    { id: "admin-assistant-stats", icon: BarChart3, label: "Assistenz-Statistik", hint: "Chatbot-Nutzung", url: "/admin/assistant-stats", roles: ["admin"] },
    { id: "admin-top-listing", icon: Rocket, label: "Top-Listing", hint: "Boost & Werbung", url: "/admin/top-listing", roles: ["admin"] },

    // — Account
    { id: "go-messages", icon: Inbox, label: "Nachrichten", hint: "Chat-Verlauf", url: "/dashboard?tab=messages", roles: ["b2c", "b2b", "vermittler"] },
    { id: "go-profile", icon: Users, label: "Profil & Einstellungen", hint: "Stammdaten, Sprache, Settings", url: "/dashboard?tab=settings", roles: ["b2c", "b2b", "vermittler"] },
    { id: "go-wallet", icon: BarChart3, label: "€Punkte / Wallet", hint: "Punktestand & Auszahlungen", url: "/wallet", roles: ["b2c", "b2b", "vermittler"] },
    { id: "restart-tour", icon: Compass, label: "Onboarding-Tour neu starten", hint: "Geführter Rundgang durch die wichtigsten Funktionen", url: "__action:restart-tour", roles: ["b2c", "b2b", "vermittler", "service_provider", "admin"] },
];

export default function CommandPalette({ user }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const role = user?.role;

    // Jun-2026 (User-Wunsch) — Fahrzeug-Lookup-Modus (Kennzeichen/FIN scannen).
    // Barcode-/QR-Scanner verhalten sich wie eine Tastatur und schließen i. d. R.
    // mit Enter ab → Eingabe + Enter löst die Suche sofort aus.
    const [lookupMode, setLookupMode] = useState(false);
    const [lookupQ, setLookupQ] = useState("");
    const [lookupResults, setLookupResults] = useState([]);
    const [lookupBusy, setLookupBusy] = useState(false);
    const lookupRef = useRef(null);

    const doVehicleLookup = async (raw) => {
        const v = (raw ?? lookupQ).trim();
        if (v.length < 2) { setLookupResults([]); return; }
        setLookupBusy(true);
        try {
            const r = await api.get(`/dealer/vehicles?q=${encodeURIComponent(v)}&limit=8`);
            setLookupResults((r.data?.items || r.data || []).slice(0, 8));
        } catch {
            setLookupResults([]);
        } finally {
            setLookupBusy(false);
        }
    };

    const pickVehicle = (veh) => {
        setOpen(false);
        if (veh.customer_id) {
            navigate(`/dealer/workshop-orders?action=new&customer=${veh.customer_id}&vehicle=${veh.id}`);
        } else {
            navigate(`/dealer/workshop-orders?action=new`);
        }
    };

    // Hotkey: Cmd+K / Ctrl+K / „/" oder per Custom-Event vom FAB
    useEffect(() => {
        const onKeyDown = (e) => {
            const isMod = e.metaKey || e.ctrlKey;
            if (isMod && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            } else if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA" && !document.activeElement?.isContentEditable) {
                e.preventDefault();
                setOpen(true);
            } else if (e.key === "Escape" && open) {
                e.preventDefault();
                setOpen(false);
            }
        };
        const onCustomOpen = () => setOpen(true);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("twowheels:open-palette", onCustomOpen);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("twowheels:open-palette", onCustomOpen);
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            setQuery("");
            setSearchResults([]);
            setSelectedIdx(0);
            setLookupMode(false);
            setLookupQ("");
            setLookupResults([]);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Fokus + Debounce-Suche im Fahrzeug-Lookup-Modus
    useEffect(() => {
        if (lookupMode) setTimeout(() => lookupRef.current?.focus(), 50);
    }, [lookupMode]);

    useEffect(() => {
        if (!lookupMode) return;
        const t = setTimeout(() => { doVehicleLookup(lookupQ); }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookupQ, lookupMode]);

    // Live-Search nach eigenen Inseraten ab 2 Buchstaben
    useEffect(() => {
        if (!open || query.trim().length < 2 || !user) {
            setSearchResults([]);
            return;
        }
        const ctl = new AbortController();
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/listings?owner_id=${user.id}&q=${encodeURIComponent(query)}`, { signal: ctl.signal });
                setSearchResults((r.data || []).slice(0, 5));
            } catch { /* ignore */ }
        }, 250);
        return () => { clearTimeout(t); ctl.abort(); };
    }, [query, open, user]);

    const filteredActions = useMemo(() => {
        const q = query.trim().toLowerCase();
        return ACTIONS
            .filter((a) => !a.roles || a.roles.includes(role) || (a.roles.includes("b2b") && role === "admin"))
            .filter((a) => !q
                || a.label.toLowerCase().includes(q)
                || (a.hint || "").toLowerCase().includes(q))
            .sort((a, b) => (b.rank || 0) - (a.rank || 0))
            .slice(0, 12);
    }, [query, role]);

    const combinedRows = useMemo(() => {
        const rows = filteredActions.map((a) => ({ kind: "action", data: a }));
        searchResults.forEach((l) => rows.push({ kind: "listing", data: l }));
        return rows;
    }, [filteredActions, searchResults]);

    useEffect(() => {
        setSelectedIdx((i) => Math.min(i, Math.max(0, combinedRows.length - 1)));
    }, [combinedRows.length]);

    const runRow = (row) => {
        if (!row) return;
        if (row.kind === "action") {
            const url = row.data.url || "";
            if (url.startsWith("__action:")) {
                const actionId = url.slice("__action:".length);
                if (actionId === "restart-tour") {
                    try {
                        Object.keys(localStorage)
                            .filter((k) => k.startsWith("twowheels.tour.completed."))
                            .forEach((k) => localStorage.removeItem(k));
                    } catch { /* ignore */ }
                    window.dispatchEvent(new CustomEvent("twowheels:start-tour"));
                } else if (actionId === "vehicle-lookup") {
                    setLookupMode(true);
                    return; // Palette bleibt offen, wechselt in den Lookup-Modus
                }
            } else {
                navigate(url);
            }
        } else if (row.kind === "listing") navigate(`/listings/${row.data.id}`);
        setOpen(false);
    };

    const onKeyDownInput = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIdx((i) => Math.min(combinedRows.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIdx((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            runRow(combinedRows[selectedIdx]);
        }
    };

    if (!user || !open) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center pt-[10vh] px-4" data-testid="command-palette">
            <div className="bg-white border-2 border-[#FFCC00] rounded-sm shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                {lookupMode ? (
                    <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-emerald-50" data-testid="palette-lookup-header">
                        <button
                            type="button"
                            onClick={() => { setLookupMode(false); setTimeout(() => inputRef.current?.focus(), 50); }}
                            className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 shrink-0"
                            data-testid="palette-lookup-back"
                        >
                            ← Zurück
                        </button>
                        <Search className="w-5 h-5 text-emerald-700 shrink-0" />
                        <input
                            ref={lookupRef}
                            type="text"
                            value={lookupQ}
                            onChange={(e) => setLookupQ(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); doVehicleLookup(lookupQ); }
                                else if (e.key === "Escape") { e.preventDefault(); setLookupMode(false); }
                            }}
                            placeholder="Kennzeichen oder FIN scannen / eingeben…"
                            className="flex-1 bg-transparent outline-none text-base font-medium placeholder:text-emerald-700/50 uppercase"
                            data-testid="palette-lookup-input"
                        />
                        {lookupBusy && <span className="text-[10px] text-emerald-700 shrink-0">Suche…</span>}
                    </div>
                ) : (
                <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-[#FFCC00]/10">
                    <Command className="w-5 h-5 text-[#111827] shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDownInput}
                        placeholder="Aktion oder Inserat suchen… (z. B. 'Inserat anlegen', 'BMW R 1250', 'Auktion')"
                        className="flex-1 bg-transparent outline-none text-base font-medium placeholder:text-gray-400"
                        data-testid="palette-input"
                    />
                    <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 bg-white border border-gray-300 rounded px-1.5 py-0.5">ESC</kbd>
                </div>
                )}

                {lookupMode ? (
                    <div className="overflow-y-auto" data-testid="palette-lookup-results">
                        {lookupResults.length === 0 ? (
                            <div className="px-6 py-10 text-center text-sm text-gray-500" data-testid="palette-lookup-empty">
                                {lookupQ.trim().length < 2 ? "Kennzeichen oder FIN scannen / eingeben…" : (lookupBusy ? "Suche…" : "Kein Fahrzeug gefunden.")}
                            </div>
                        ) : (
                            <div className="py-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 px-4 py-1">Gefundene Fahrzeuge</div>
                                {lookupResults.map((v) => (
                                    <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => pickVehicle(v)}
                                        className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-emerald-50"
                                        data-testid={`palette-lookup-vehicle-${v.id}`}
                                    >
                                        <Wrench className="w-4 h-4 shrink-0 text-emerald-600" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm truncate">{v.title || `${v.brand || ""} ${v.model || ""}`.trim() || "Fahrzeug"}</div>
                                            <div className="text-[11px] text-gray-500 truncate font-mono-num">
                                                {[v.license_plate, v.vin].filter(Boolean).join(" · ") || "—"}
                                                {v.customer_name ? ` · ${v.customer_name}` : (v.kind === "inventory" ? " · Bestand" : "")}
                                            </div>
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 shrink-0">Auftrag →</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                <div className="overflow-y-auto" data-testid="palette-results">
                    {filteredActions.length > 0 && (
                        <div className="py-2">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 px-4 py-1">Schnell-Aktionen</div>
                            {filteredActions.map((a, idx) => {
                                const Icon = a.icon;
                                const isSel = idx === selectedIdx;
                                return (
                                    <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => runRow({ kind: "action", data: a })}
                                        onMouseEnter={() => setSelectedIdx(idx)}
                                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${isSel ? "bg-[#FFCC00]/20" : "hover:bg-gray-50"}`}
                                        data-testid={`palette-action-${a.id}`}
                                    >
                                        <Icon className={`w-4 h-4 shrink-0 ${isSel ? "text-[#111827]" : "text-gray-500"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm truncate">{a.label}</div>
                                            <div className="text-[11px] text-gray-500 truncate">{a.hint}</div>
                                        </div>
                                        {a.rank >= 95 && (
                                            <span className="hidden sm:inline-flex shrink-0 text-[9px] uppercase tracking-wider font-bold bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm">Top</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {searchResults.length > 0 && (
                        <div className="py-2 border-t border-gray-100">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 px-4 py-1">Deine Inserate</div>
                            {searchResults.map((l, j) => {
                                const idx = filteredActions.length + j;
                                const isSel = idx === selectedIdx;
                                return (
                                    <button
                                        key={l.id}
                                        type="button"
                                        onClick={() => runRow({ kind: "listing", data: l })}
                                        onMouseEnter={() => setSelectedIdx(idx)}
                                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${isSel ? "bg-[#FFCC00]/20" : "hover:bg-gray-50"}`}
                                        data-testid={`palette-listing-${l.id}`}
                                    >
                                        {l.image_urls?.[0] ? (
                                            <img src={l.image_urls[0]} alt="" className="w-10 h-10 object-cover rounded-sm shrink-0" />
                                        ) : (
                                            <div className="w-10 h-10 bg-gray-100 rounded-sm shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm truncate">{l.title || `${l.brand || ""} ${l.model || ""}`}</div>
                                            <div className="text-[11px] text-gray-500 truncate font-mono-num">
                                                {l.price ? `${Number(l.price).toLocaleString("de-DE")} €` : ""} · {l.sale_type || "fixed"}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {combinedRows.length === 0 && (
                        <div className="px-6 py-12 text-center text-sm text-gray-500" data-testid="palette-empty">
                            Keine Treffer für „{query}"
                        </div>
                    )}
                </div>
                )}

                <div className="border-t border-gray-200 p-2 px-4 bg-gray-50 flex items-center justify-between text-[10px] text-gray-500">
                    <span className="flex items-center gap-2">
                        <kbd className="font-bold bg-white border border-gray-300 rounded px-1">↑↓</kbd> navigieren
                        <kbd className="font-bold bg-white border border-gray-300 rounded px-1">↵</kbd> auswählen
                    </span>
                    <span>
                        <kbd className="font-bold bg-white border border-gray-300 rounded px-1">⌘K</kbd> oder
                        <kbd className="font-bold bg-white border border-gray-300 rounded px-1 ml-1">/</kbd> öffnen
                    </span>
                </div>
            </div>
        </div>
    );
}

/**
 * Floating "Quick Actions" Trigger — sichtbar als ⚡ FAB unten rechts.
 * Erlaubt Maus-Nutzern ebenfalls Zugang zur Palette.
 */
export function CommandPaletteFAB() {
    const dispatch = () => {
        window.dispatchEvent(new CustomEvent("twowheels:open-palette"));
    };
    return (
        <button
            type="button"
            onClick={dispatch}
            className="fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-[#111827] text-[#FFCC00] shadow-xl hover:scale-110 active:scale-95 transition-transform flex items-center justify-center"
            title="Quick-Actions öffnen (⌘K oder /)"
            data-testid="command-palette-fab"
            aria-label="Quick-Actions"
        >
            <Command className="w-5 h-5" />
        </button>
    );
}
